/**
 * React hooks over the pure **live scene tree** (`liveSceneTree.ts`). They supply the
 * loader's scene and GLB cache snapshots, and re-render when the tree grows because an
 * instance sub-scene or GLB finishes loading after the parsed SceneGraph was built.
 */
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useOptionalHierarchy } from './contexts/HierarchyContext.js';
import { useResourceLoader } from '../resources/useResource.js';
import { collectLiveNodes, liveTreeOf, resolveLiveEntry, } from './liveSceneTree.js';
import { liveInstancePath } from '../resources/liveNode.js';
import { glbPathOf } from '../resources/glbSceneRoot.js';
import { perObject } from '../utils/perObject.js';
import { sameElements } from '../utils/sameElements.js';
import { subscribeToNothing } from '../utils/subscribeToNothing.js';
import { NOTHING_CACHED } from './cacheSources.js';
import { useProjectSettings } from './contexts/ProjectSettingsContext.js';
import { projectPhysicsClock } from '../parser/projectSettingsParser.js';
/** The caches of `loader` that a live-tree context reads. With no loader, every path reads as never requested. */
function loaderCaches(loader) {
    return { sceneCache: loader?.scenes ?? NOTHING_CACHED, glbCache: loader?.glbMeshes };
}
/**
 * Build the live-tree roots and context from the parsed SceneGraph, the loader
 * cache snapshots and the project's physics clock. `null` while the root scene is not available.
 */
function liveTreeContext(sceneGraph, loader, physicsClock) {
    // `scenes` is optional-chained too: readers outside the canvas (the toolbar)
    // see whatever the host put in HierarchyContext, which is not always a fully
    // built SceneGraph.
    const root = sceneGraph?.scenes?.get(sceneGraph.rootScene);
    if (!root)
        return null;
    return {
        roots: root.nodes,
        ctx: {
            externalResources: root.externalResources,
            internalResources: root.internalResources,
            ...loaderCaches(loader),
            physicsClock,
        },
    };
}
const LIVE_TREE_BUSES = ['scene', 'glb'];
const NO_LOAD_TICKS = { version: 0, subscribe: subscribeToNothing, getVersion: () => 0 };
/**
 * The one load counter of a loader, which every reader of its live tree shares. A handler sits on
 * the loader's bus only while a reader is mounted, so loads go unseen before the first subscribe
 * and after the last unsubscribe. The version moves at both, so no reader trusts a tree built then.
 */
const loadTicksOfLoader = perObject((loader) => {
    const listeners = new Set();
    let stopListening;
    const ticks = {
        version: 0,
        getVersion: () => ticks.version,
        subscribe: (onChange) => {
            if (listeners.size === 0)
                ticks.version++;
            listeners.add(onChange);
            stopListening ??= loader.eventBus.onChange(LIVE_TREE_BUSES, ({ event }) => {
                if (event === 'invalidated')
                    return;
                ticks.version++;
                for (const listener of listeners)
                    listener();
            });
            return () => {
                listeners.delete(onChange);
                if (listeners.size > 0)
                    return;
                stopListening?.();
                stopListening = undefined;
                ticks.version++;
            };
        },
    };
    return ticks;
});
function loadTicksOf(loader) {
    return loader ? loadTicksOfLoader(loader) : NO_LOAD_TICKS;
}
/**
 * A counter that moves whenever a scene or GLB loads or fails, so a deleted
 * instance leaves the tree. A reader of the whole tree re-derives on it; {@link useLiveTreeSelection}
 * re-renders only where its value changed. The scenes and
 * GLBs it reads are `useResource` consumers' keys, which they request again. It is one
 * number per loader, so two readers on one load tick see the same value.
 */
export function useLiveTreeVersion(loader) {
    const ticks = loadTicksOf(loader);
    return useSyncExternalStore(ticks.subscribe, ticks.getVersion);
}
/** The key of a reader with no loader, which a WeakMap cannot take as null. */
const NO_LOADER = {};
/**
 * A reader of the value that `build` makes for a loader. It builds again once the loader's load tick
 * has moved, and keeps the last value when `isSame` says the new one matches it. A reader with no
 * loader builds once, at tick 0.
 */
function valueAtLoadTick(build, isSame = Object.is) {
    const built = new WeakMap();
    return (loader, version) => {
        const known = built.get(loader ?? NO_LOADER);
        if (known?.version === version)
            return known.value;
        const next = build(loader, version);
        const value = known && isSame(known.value, next) ? known.value : next;
        built.set(loader ?? NO_LOADER, { version, value });
        return value;
    };
}
/**
 * The one tree every reader of a scene graph and physics clock over a loader shares for a load
 * tick. `projectPhysicsClock` hands back one clock for each pair of values, so the key holds.
 */
const sharedTreesOf = perObject((sceneGraph) => perObject((physicsClock) => valueAtLoadTick((loader) => liveTreeContext(sceneGraph, loader, physicsClock))));
/** The shared tree of what {@link useLiveTreeInputs} read, at the load tick `version`. */
function sharedTreeAt({ sceneGraph, loader, physicsClock }, version) {
    return sharedTreesOf(sceneGraph)(physicsClock)(loader, version);
}
/**
 * The project's physics clock, Godot's defaults without a project. Every live tree reads it
 * (`LiveTreeContext.physicsClock`), and one settings object hands back one clock.
 */
export function useProjectPhysicsClock() {
    return projectPhysicsClock(useProjectSettings().settings);
}
/** The scene graph in context, or null with none, the loader and the project's physics clock. */
function useLiveTreeInputs() {
    // Optional so viewport-level readers (the preview lighting) work in the
    // empty-canvas and test-renderer cases, where no scene is mounted at all.
    const sceneGraph = useOptionalHierarchy()?.sceneGraph ?? null;
    const loader = useResourceLoader();
    const physicsClock = useProjectPhysicsClock();
    return { sceneGraph, loader, physicsClock };
}
/**
 * The live tree of the scene graph in context, or null with no root scene. Every reader asks for
 * the one the graph, loader and load tick give, so a node collapses once however many ask.
 */
export function useSharedLiveTree() {
    const { sceneGraph, loader, physicsClock } = useLiveTreeInputs();
    const version = useLiveTreeVersion(loader);
    return useMemo(() => (sceneGraph ? sharedTreeAt({ sceneGraph, loader, physicsClock }, version) : null), [sceneGraph, physicsClock, loader, version]);
}
/**
 * Whether two values that may be undefined are the same: undefined equals only undefined, and two
 * values go to `isSame`.
 *
 * @canonical Compare two values that may be undefined
 */
export function sameSelection(a, b, isSame) {
    return a === undefined || b === undefined ? a === b : isSame(a, b);
}
/**
 * A value read at each load tick of `watched`. A load tick re-reads it, and the reader re-renders
 * only where `isSame` says the value changed. A reader that watches no loader reads at tick 0 once.
 * `readAt` and `isSame` must be stable.
 */
function useReadAtLoadTick(watched, readAt, isSame) {
    const ticks = loadTicksOf(watched);
    const getSnapshot = useMemo(() => {
        const at = valueAtLoadTick((_loader, version) => readAt(version), isSame);
        return () => at(watched, ticks.getVersion());
    }, [watched, ticks, readAt, isSame]);
    return useSyncExternalStore(ticks.subscribe, getSnapshot);
}
/**
 * A value read from the shared live tree, undefined with no tree. A load tick re-reads it, and the
 * reader re-renders only where `isSame` says the value changed, so a reader whose answer a load
 * leaves alone costs no render. A null `select` asks nothing, so it watches no load. `select` and
 * `isSame` must be stable.
 */
export function useLiveTreeSelection(select, isSame) {
    const { sceneGraph, loader, physicsClock } = useLiveTreeInputs();
    const readAt = useCallback((version) => {
        if (!sceneGraph || !select)
            return undefined;
        const tree = sharedTreeAt({ sceneGraph, loader, physicsClock }, version);
        return tree ? select(tree) : undefined;
    }, [sceneGraph, physicsClock, loader, select]);
    const isSameSelection = useCallback((a, b) => sameSelection(a, b, isSame), [isSame]);
    return useReadAtLoadTick(select ? loader : null, readAt, isSameSelection);
}
function seatedRootsOf({ roots, ctx }) {
    return liveTreeOf(roots, ctx).seatedRoots;
}
/**
 * The root nodes of the scene graph in context with the headings their instances hand back seated,
 * undefined with no root scene. Roots with no such heading are the graph's own array. A load re-renders
 * the reader only where it moves a heading.
 *
 * @canonical The root nodes a viewport or a tree row draws
 */
export function useSeatedRoots() {
    return useLiveTreeSelection(seatedRootsOf, sameElements);
}
const NO_ROOTS = [];
/**
 * The tree over the caches of a loader, with no roots, for each physics clock: a question about one
 * node needs only the caches. Every step question of a load tick shares it.
 */
const loaderTreesOf = perObject((physicsClock) => valueAtLoadTick((loader) => liveTreeOf(NO_ROOTS, {
    externalResources: [],
    internalResources: [],
    ...loaderCaches(loader),
    physicsClock,
})));
/**
 * The step of `node` where it sits in `scope`, over the loader's caches. A reader re-renders only for
 * a load that changes its step, since the tree hands the same step back while every answer it read is
 * unchanged. A node that instances nothing reads no cache, so it watches no load. `scope` must be stable.
 *
 * @canonical The step of a node of the Live scene tree in a React reader
 */
export function useLiveStepOf(node, scope) {
    const loader = useResourceLoader();
    const physicsClock = useProjectPhysicsClock();
    const readsCaches = liveInstancePath(node, scope) !== null || glbPathOf(node) !== undefined;
    const watched = readsCaches ? loader : null;
    const readAt = useCallback((version) => loaderTreesOf(physicsClock)(watched, version).stepOf(node, scope), [physicsClock, watched, node, scope]);
    return useReadAtLoadTick(watched, readAt, Object.is);
}
/** The live nodes matching `predicate`. Pass a stable `predicate` (module-level or memoised), or the memo recomputes every render. */
export function useLiveSceneNodes(predicate, 
/** Stable, like `predicate`: return false to skip a node's children. */
descend) {
    const liveTree = useSharedLiveTree();
    return useMemo(() => (liveTree ? collectLiveNodes(liveTree.roots, liveTree.ctx, predicate, descend) : []), [liveTree, predicate, descend]);
}
/**
 * The live-tree node at `path`: its effective (collapsed) identity (Instance root
 * merge, ADR-0013) and its `instanceRef`, descending into sub-scenes and GLBs.
 * A node inside a sub-scene resolves the moment that sub-scene lands. `null` for an
 * empty path or an unresolvable segment.
 */
export function useLiveNode(path) {
    const liveTree = useSharedLiveTree();
    return useMemo(() => (path && liveTree ? resolveLiveEntry(path, liveTree.roots, liveTree.ctx) : null), [liveTree, path]);
}
//# sourceMappingURL=useLiveSceneTree.js.map